import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { DeprecatedConfirmationDialogComponent, StepComponent } from '@app/components';
import { AnalyticEvents, FeatureFlag } from '@app/enums';
import { RoutingStateService } from '@app/modules/common/services/routing-state.service';
import { TemplateType } from '@app/modules/performance/enums/template-type.enum';
import { getSelectedParticipants } from '@app/modules/performance/functions';
import { AnalyticService } from '@app/services';
import { AuthService } from '@app/services/auth.service';
import { FeatureService } from '@app/services/feature.service';
import { NotifyService } from '@app/services/notify.service';
import { StoreView } from '@common/views';
import { Review } from '@models/performance/review.model';
import { Template } from '@models/performance/template.model';

@Component({
    templateUrl: './review-store.template.html',
    styleUrls: ['./review-store.style.scss'],
})
export class ReviewStoreView extends StoreView implements OnInit {
    @ViewChild('revokeConfirmationDialog') revokeConfirmationDialog: DeprecatedConfirmationDialogComponent;
    isLoading = false;
    missingTemplates = false;
    review: Review;
    participants: Map<number, boolean> = new Map<number, boolean>();
    parentUrl: string;
    hasPerformanceModuleSplit = false;

    constructor(
        private location: Location,
        private auth: AuthService,
        private notify: NotifyService,
        private analyticsService: AnalyticService,
        private routingState: RoutingStateService,
        private featureService: FeatureService
    ) {
        super();
        this.review = new Review({ reoccuring: 'never' });
    }

    async ngOnInit(): Promise<void> {
        this.hasPerformanceModuleSplit = await this.featureService.has(FeatureFlag.performanceModuleSplit);
        this.checkMissingTemplates();
    }

    onSave(): void {
        this.isLoading = true;
        this.review
            .attach('participants', getSelectedParticipants(this.participants))
            .save()
            .then(() => {
                this.notify.success('performance.review-store.created');
                if (this.routingState.previousUrl?.includes('overview')) {
                    this.analyticsService.trackEvent(AnalyticEvents.PerformanceOverviewCreateReviewClickCompleteToSave);
                }
                if (this.routingState.previousUrl?.includes('reviews')) {
                    this.analyticsService.trackEvent(AnalyticEvents.PerformanceReviewCreateReviewClickCompleteToSave);
                }
                if (this.routingState.previousUrl?.includes('employees')) {
                    this.analyticsService.trackEvent(
                        AnalyticEvents.EmployeeProfilePerformanceCreateReviewClickCompleteToSave
                    );
                }
                this.location.back();
            })
            .catch((err) => {
                this.isLoading = false;
                this.notify.error(err);
            });
    }

    async onNextStep([currentStep]: [StepComponent, number]): Promise<void> {
        switch (currentStep.step) {
            case 'Template':
                this.analyticsService.trackEvent(AnalyticEvents.PerformanceAllPathsToCreateReviewStep1ClickNext);
                break;
            case 'Participants':
                this.analyticsService.trackEvent(AnalyticEvents.PerformanceAllPathsToCreateReviewStep2ClickNext);
                break;
            case 'Summary':
                this.analyticsService.trackEvent(AnalyticEvents.PerformanceAllPathsToCreateReviewStep3ClickNext);
                break;
        }
    }

    async onPreviousStep([currentStep]: [StepComponent, number]): Promise<void> {
        switch (currentStep.step) {
            case 'New Review':
                this.analyticsService.trackEvent(AnalyticEvents.PerformanceAllPathsToCreateReviewStep2ClickPrevious);
                break;
            case 'Template':
                this.analyticsService.trackEvent(AnalyticEvents.PerformanceAllPathsToCreateReviewStep3ClickPrevious);
                break;
            case 'Participants':
                this.analyticsService.trackEvent(AnalyticEvents.PerformanceAllPathsToCreateReviewStep4ClickPrevious);
                break;
        }
    }

    private async checkMissingTemplates(): Promise<void> {
        let query = Template.param('company', this.auth.company.id);

        query = this.hasPerformanceModuleSplit ? query.where('type', TemplateType.REVIEW) : query;

        const [templates] = await query.all();

        this.missingTemplates = templates?.length === 0;
    }
}
